import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FiX } from "react-icons/fi";
import {
  useEditRatingScaleMutation,
  useGetSingleRatingQuery,
} from "../../../../../api/hrApi";
import DashboardStyle from "../../../../../dashboard/Styles/Dashboard.module.css";
import { ActionButtons } from "../../../../../global/components/Buttons/buttons";
import {
  FormInput,
  FormSelect,
} from "../../../../Components/Forms/InputTemplate";
import { toast } from "react-hot-toast";

const levelOptions = Array.from({ length: 10 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString(),
}));

const colorOptions = [
  { value: 0, label: "Red" },
  { value: 1, label: "Yellow" },
  { value: 2, label: "Blue" },
  { value: 3, label: "Black" },
  { value: 4, label: "White" },
  { value: 5, label: "Green" },
];
const EditRatingScaleModal = ({
  setIsOpen,
  isOpen,
  onValidate = () => {},
  id,
}) => {
  const { data } = useGetSingleRatingQuery(id);
  const [onCreate, { isLoading }] = useEditRatingScaleMutation();
  const [levels, setLevels] = useState(
    data?.responseObject?.responseObject?.scaleLevel ?? 1
  );

  const formMethods = useForm({
    mode: "all",
    defaultValues: {
      title: "",
      scaleLevel: 1,
      scaleLevels: [],
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
    watch,
  } = formMethods;

  // Add this useEffect to update the form when data is fetched
  useEffect(() => {
    if (data?.responseObject) {
      reset({
        title: data.responseObject.title || "",
        scaleLevel: data.responseObject.scaleLevel || 1,
        scaleLevels: (data.responseObject.scaleLevels || []).map((item, i) => ({
          scaleNumber: item.scaleNumber || i + 1,
          scaleTitle: item.scaleTitle || "",
          scaleDescription: item.scaleDescription || "",
          colorType:
            typeof item.colorType === "string"
              ? colorOptions.find((c) => c.label === item.colorType)?.value ?? 0
              : item.colorType ?? 0,
        })),
      });
      setLevels(data.responseObject.scaleLevel || 1);
    }
  }, [data, reset]);

  // Watch for changes in levels and update scaleLevels array accordingly
  const scaleLevels = watch("scaleLevels");
  const scaleLevel = watch("scaleLevel");

  // Update scaleLevels array length when levels changes
  const handleLevelsChange = (e) => {
    const newLevel = Number(e.target.value);
    setLevels(newLevel);
    setValue("scaleLevel", newLevel);

    // Adjust the scaleLevels array to match the selected level count
    const updatedLevels = Array.from({ length: newLevel }, (_, i) => {
      return (
        scaleLevels?.[i] || {
          scaleNumber: i + 1,
          scaleTitle: "",
          scaleDescription: "",
          colorType: 0,
        }
      );
    });
    setValue("scaleLevels", updatedLevels);
  };

  const submit = async (data) => {
    const payload = {
      id,
      title: data.title,
      scaleLevel: Number(data.scaleLevel),
      scaleLevels: data.scaleLevels.map((level, idx) => ({
        scaleNumber: Number(level.scaleNumber ?? idx + 1),
        scaleTitle: level.scaleTitle,
        scaleDescription: level.scaleDescription,
        colorType: Number(level.colorType),
      })),
    };
    if (!id) {
      toast.error("Rating Scale ID is required");
      return;
    }
    try {
      const response = await onCreate(payload).unwrap();
      if (response.successful) {
        toast.success(response?.statusMessage);
        setIsOpen(false);
        onValidate();
      }
    } catch (err) {
      toast.error(err.data.statusMessage);
    }
  };

  return (
    <div className={DashboardStyle.dash_board_home_nav}>
      <div className={DashboardStyle.dash_board_home_nav_header}>
        <div>
          <h4 className="text-[25px] font-[500]">Edit </h4>
          <h1 className="text-[28px] font-[800]">Rating Scale</h1>
        </div>
        <FiX
          style={{ cursor: "pointer" }}
          size={"1.5rem"}
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>
      <div className={DashboardStyle.dash_board_home_nav_body}>
        {isLoading && <small>Loading...</small>}
        <div>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(submit)} className="space-y-6">
              <FormInput
                camelCase="title"
                title="Title"
                placeholder="Enter rating scale title"
              />
              <FormSelect
                camelCase="scaleLevel"
                title="Number of Levels"
                array={levelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                defaultValue={levels}
                moreRegister={{
                  onChange: handleLevelsChange,
                }}
              />
              {Array.from({ length: Number(scaleLevel) || 1 }).map((_, idx) => (
                <div
                  key={idx}
                  className="border rounded p-3 mb-2 bg-gray-50 flex flex-col gap-2"
                >
                  <div className="font-semibold text-sm mb-1">
                    Level {idx + 1}
                  </div>
                  <FormInput
                    camelCase={`scaleLevels.${idx}.scaleNumber`}
                    title="Scale Number"
                    type="number"
                    defaultValue={idx + 1}
                  />
                  <FormInput
                    camelCase={`scaleLevels.${idx}.scaleTitle`}
                    title="Scale Title"
                    placeholder="Enter scale title"
                  />
                  <FormInput
                    camelCase={`scaleLevels.${idx}.scaleDescription`}
                    title="Scale Description"
                    placeholder="Enter scale description"
                  />
                  <FormSelect
                    camelCase={`scaleLevels.${idx}.colorType`}
                    title="Color"
                    array={colorOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    defaultValue={0}
                  />
                </div>
              ))}
              <div
                style={{ marginTop: "1rem" }}
                className={DashboardStyle.button_cage}
              >
                <ActionButtons
                  isLoading={isLoading}
                  disabled={!isValid}
                  width={"auto"}
                  type={"submit"}
                  // onClick={onValidate}
                  className={DashboardStyle?.button_cage_weight}
                  bg="var(--primary-color)"
                >
                  Update
                </ActionButtons>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default EditRatingScaleModal;
